import { is, List } from "immutable";
import _ from "lodash";
import * as React from "react";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import * as redux from "redux";
import { GradingForm } from "./components";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import { AbstractSummaryForGrader } from "../../../containers/abstracts/components";
import * as Forms from "../../../forms";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { AppState } from "../../../store";
import { ReactHelper } from "../../../utils";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultStatePropsMap(state, ownProps),
    abstracts: state.abstractGrading.abstracts,
    listAbstracts: state.abstractGrading.listAbstracts,
    loadingAbstract: state.abstractGrading.loadingAbstract,
    loadingListAbstracts: state.abstractGrading.loadingListAbstracts,
    loadingType: state.abstractGrading.loadingType,
    types: state.abstractGrading.types,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
    loadAbstract: (guid: string) => dispatch(Actions.AbstractGrading.loadAbstract(guid)),
    loadListAbstracts: () => dispatch(Actions.AbstractGrading.loadListAbstracts()),
    loadType: (typeId: number) => dispatch(Actions.AbstractGrading.loadType(typeId)),
    saveReviewerStats: (r: M.Abstract.ReviewerStats) => dispatch(Actions.AbstractGrading.onReviewerStatsUpdated(r)),
    toggleAbstractLoading: (guid: string, loading: boolean) => dispatch(Actions.AbstractGrading.toggleLoadingAbstract(guid, loading)),
    toggleTypeLoading: (typeId: number, loading: boolean) => dispatch(Actions.AbstractGrading.toggleLoadingType(typeId, loading)),
    updateAbstract: (a: M.Abstract.AbstractForGrader) => dispatch(Actions.AbstractGrading.updateAbstract(a)),
    updateListAbstract: (a: M.Abstract.AbstractGraderListItem) => dispatch(Actions.AbstractGrading.updateListAbstract(a)),
});

type OwnProps = RouteComponentProps<{
    guid: string;
}> & {};

type ConnectedState = Comp.Base.DefaultConnectedState & {
    abstracts: List<M.Abstract.AbstractForGrader>;
    listAbstracts: List<M.Abstract.AbstractGraderListItem>;
    loadingAbstract: M.Generic.ItemLoadingState;
    loadingListAbstracts: boolean;
    loadingType: M.Generic.ItemLoadingState;
    types: List<M.Abstract.AbstractType>;
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
    loadAbstract: (guid: string) => void;
    loadListAbstracts: () => void;
    loadType: (typeId: number) => void;
    saveReviewerStats: (r: M.Abstract.ReviewerStats) => void;
    toggleAbstractLoading: (guid: string, loading: boolean) => void;
    toggleTypeLoading: (typeId: number, loading: boolean) => void;
    updateAbstract: (a: M.Abstract.AbstractForGrader) => void;
    updateListAbstract: (a: M.Abstract.AbstractGraderListItem) => void;
};

interface OwnState {
    abstract?: M.Abstract.AbstractForGrader;
    type?: M.Abstract.AbstractType;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class DetailsComponent extends Comp.Base.BaseComponent<CombinedProps, OwnState> {

    private _abstractTypeId = 0;
    private _form: GradingForm | null = null;

    public constructor(props: any) {
        super(props);
        this.state = {
            loading: true,
        };
    }

    public async componentDidMount() {
        this._isMounted = true;

        // check user's status as a reviewer
        const hasReviewerAccess = await M.Abstract.checkReviewerAccess(this.props);
        if (!hasReviewerAccess) {
            this.props.notifyError("You do not have access to view the requested page.");
            this.props.goto(Paths.User.Account);
            return;
        }

        this.initializeComponent();
    }

    public async componentDidUpdate(prevProps: Readonly<CombinedProps>, prevState: Readonly<OwnState>) {
        if (!is(prevProps.abstracts, this.props.abstracts))
            this.loadAbstractFromState();

        if (!is(prevProps.types, this.props.types))
            this.loadTypeFromState();

        if (this._form && (!is(prevState.type, this.state.type) || !is(prevState.abstract, this.state.abstract)) && this.state.type && this.state.abstract) {
            await this.setStateAsync({ ...this.state, loading: false });
            this._form.setFormData(this.state.abstract, this.state.type);
        }

        // just in case a different guid in URL has been detected
        if (this.getGuid(prevProps) !== this.getGuid()) {
            if (this._form) {
                this._form.resetFormData(() => {
                    this.clearState(() => {
                        this.initializeComponent();
                    });
                });
            }
        }
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        const guid = this.getGuid();
        const loading = this.state.loading || !!this.props.loadingAbstract[guid] || !!this.props.loadingType[this._abstractTypeId];

        const summary = this.state.abstract && this.state.type
            ? <AbstractSummaryForGrader
                abstract={this.state.abstract}
                type={this.state.type}
            />
            : null;

        const backLinkText = <>&laquo; Return to Grade Abstracts</>;
        const backLink = this.lastPathIsFromListView()
            ? <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); window.history.back(); }}>{backLinkText}</a>
            : <Link to={Paths.User.AbstractGrading.Abstracts}>{backLinkText}</Link>;

        return <Comp.Page.PublicPage additionalClasses="full" pageTitle="Abstract details" style={{ minHeight: "150px", position: "relative" }}>
            <Comp.Generic.LoadingSpinner show={loading} />

            <Comp.Layout.ContentPrimary>

                {this.getErrorAlert()}

                <p>{backLink}</p>

                <div className="grade">
                    <div className="grade-primary">
                        {summary}
                    </div>
                    <div className="grade-secondary">
                        <GradingForm
                            api={this.props.api}
                            onSubmit={(model, body) => this.saveGrade(model, body)}
                            ref={(ref) => this._form = ref}
                        />
                    </div>
                </div>

            </Comp.Layout.ContentPrimary>

            <Comp.Layout.ContentSecondary>

            </Comp.Layout.ContentSecondary>
            <div className="clear"></div>
        </Comp.Page.PublicPage>;
    }

    private clearState(callback?: () => void) {
        this.setState({
            abstract: undefined,
            type: undefined,
        }, callback);
    }

    private getGuid(nextProps?: Readonly<CombinedProps>) {
        return nextProps ? nextProps.match.params.guid : this.props.match.params.guid;
    }

    private getNextUngradedAbstractFrom(guid: string) {
        const _abstract = this.props.listAbstracts.find((x: M.Abstract.AbstractGraderListItem) => x.guid === guid);
        if (_abstract) {
            let abstracts = _.orderBy(this.props.listAbstracts
                .map((x: M.Abstract.AbstractGraderListItem) => ({ id: x.id, isGraded: M.Abstract.GradingStatuses.isGraded(x) }))
                .toArray(), "id");

            // double up the array
            abstracts = abstracts.concat(abstracts);

            const currIdx = _.findIndex(abstracts, (x) => x.id === _abstract.id);
            if (currIdx > -1 && abstracts.length > 2) {
                const nextIdx = _.findIndex(abstracts, (x) => x.id !== _abstract.id && !x.isGraded, currIdx);
                if (nextIdx === -1 || abstracts[nextIdx] === undefined)
                    return undefined;

                const nextAbstractId = abstracts[nextIdx].id;
                return this.props.listAbstracts.find((x: M.Abstract.AbstractGraderListItem) => x.id === nextAbstractId);
            }
        }

        return undefined;
    }

    private async initializeComponent() {

        // first get type ID by abstract GUID
        const guid = this.getGuid();

        // check abstract access
        const hasAccessToAbstract = await M.Abstract.checkAbstractAccess(this.props.api, this.getGuid());
        if (!hasAccessToAbstract) {
            this.props.notifyError(`Access to grade abstract ${guid} is denied.`);
            this.props.goto(Paths.User.AbstractGrading.Abstracts);
            return;
        }

        // first check if we can get abstract type ID from state
        const _abstract = this.props.abstracts.find((x: M.Abstract.Abstract) => x.guid.toLowerCase() === guid.toLowerCase());
        if (_abstract) {
            this._abstractTypeId = _abstract.typeId;
            this.props.loadType(this._abstractTypeId);
        }
        else {
            // if not, get it from api
            const typeIdResult = await this.props.api.get(Api.endpoints.abstract.typeIdByAbstractGuid.withUrlParams({ guid }));

            if (!this._isMounted)
                return;

            if (!typeIdResult.isSuccess) {
                this.setErrors(M.Result.Result.errorsOrMessage(typeIdResult));
                return;
            }

            this._abstractTypeId = typeIdResult.data || 0;
            this.props.loadType(this._abstractTypeId);
        }

        this.loadDataFromState();
        this.props.loadAbstract(guid);

        if (!this.props.listAbstracts.size)
            this.props.loadListAbstracts();
    }

    private lastPathIsFromListView() {
        const basePath = Paths.User.AbstractGrading.Abstracts;
        return this.props.lastPath === basePath || (this.props.lastPath || "").indexOf(`${basePath}?`) === 0;
    }

    private async loadDataFromState() {
        const guid = this.getGuid();
        let abstract: M.Abstract.AbstractForGrader | undefined;
        let type: M.Abstract.AbstractType | undefined;

        if (this.props.abstracts.size) {
            abstract = this.props.abstracts.find((x: M.Abstract.AbstractForGrader) => x.guid.toLowerCase() === guid.toLowerCase());
        }

        if (this.props.types.size)
            type = this.props.types.find((x: M.Abstract.AbstractType) => x.id === this._abstractTypeId);

        await this.setStateAsync({
            ...this.state,
            abstract,
            type,
        });

        // toggle loading of data after state set
        if (!abstract)
            this.props.toggleAbstractLoading(guid, true);
        if (!type)
            this.props.toggleTypeLoading(this._abstractTypeId, true);
    }

    private async loadAbstractFromState() {
        if (this.props.abstracts.size) {
            const guid = this.getGuid();
            const abstract = this.props.abstracts.find((x: M.Abstract.Abstract) => x.guid.toLowerCase() === guid.toLowerCase());
            if (abstract) {
                await this.setStateAsync({
                    ...this.state,
                    abstract,
                });
            }
        }
    }

    private loadTypeFromState() {
        if (this.props.types.size) {
            const type = this.props.types.find((x: M.Abstract.AbstractType) => x.id === this._abstractTypeId);
            if (type) {
                this.setState({
                    ...this.state,
                    type,
                });
            }
        }
    }

    private async saveGrade(model: Forms.Abstract.GradeData, body: any) {
        if (!this.state.abstract)
            return;

        if (this._form)
            this._form.toggleProcessing(true);

        const guid = this.getGuid();
        const nextUngradedAbstract = this.getNextUngradedAbstractFrom(guid);
        const _abstract = this.state.abstract;
        const _listAbstract = this.props.listAbstracts.find((x: M.Abstract.AbstractGraderListItem) => x.guid === guid);
        const result = await this.props.api.post(Api.endpoints.users.abstractGrading.grade.withUrlParams({ guid }), {
            body: JSON.stringify(body),
        });

        if (!this._isMounted || !this._form)
            return;

        this._form.toggleProcessing(false);

        if (result.isSuccess && result.data) {
            if (_listAbstract) {
                _listAbstract.gradingStatusId = M.Abstract.GradingStatuses.Graded.id;
                this.props.updateListAbstract(_listAbstract);
            }

            _abstract.grade = result.data;
            this.props.updateAbstract(_abstract);

            this.props.notifySuccess(`Abstract <strong>#${_abstract.id}</strong> has been successfully graded.`);

            // go to next ungraded abstract if there is one, otherwise to list view
            this.props.goto(nextUngradedAbstract
                ? Paths.UserAbstractGradingDetails(nextUngradedAbstract.guid)
                : Paths.User.AbstractGrading.Abstracts);
        }
        else {
            this._form.setFormErrors(M.Result.Result.errorsOrMessage(result), undefined, true);
        }
    }
}

export const Details: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(DetailsComponent) as any);



// WEBPACK FOOTER //
// ./src/containers/user/abstractGrading/Details.tsx